// src/lib/api/automation/scheduling.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling } from '../shared/errors'
import { validateRequired } from '../shared/validation'

/**
 * Schedule time-based automation
 * Migrated from supabaseApi.js lines 268-287
 */
const _scheduleAutomation = async (automationId, triggerRecordId, triggerRecordType, referenceTime, data = {}) => {
  console.log('[automation] scheduleAutomation called:', { automationId, triggerRecordId, triggerRecordType, referenceTime })
  
  validateRequired(automationId, 'Automation ID')
  validateRequired(triggerRecordId, 'Trigger record ID')
  validateRequired(triggerRecordType, 'Trigger record type')
  validateRequired(referenceTime, 'Reference time')
  
  const { data: result, error } = await supabase
    .rpc('schedule_time_based_automation', {
      p_automation_id: automationId,
      p_trigger_record_id: triggerRecordId,
      p_trigger_record_type: triggerRecordType,
      p_reference_time: referenceTime,
      p_data: data
    })

  if (error) {
    console.error('[automation] Error scheduling automation:', error)
    throw error
  }

  console.log('[automation] scheduleAutomation success, scheduled automation:', automationId)
  return { data: result }
}

/**
 * Schedule automations for a job
 * Migrated from supabaseApi.js lines 288-322
 */
const _scheduleJobAutomations = async (jobId, scheduledDateTime) => {
  console.log('[automation] scheduleJobAutomations called for job:', jobId, 'at:', scheduledDateTime)
  
  validateRequired(jobId, 'Job ID')
  validateRequired(scheduledDateTime, 'Scheduled date time')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[automation] No company ID found, skipping job automation scheduling')
    return
  }

  // Get all active time-based automations for jobs
  const { data: automations } = await supabase
    .from('automations')
    .select('*')
    .eq('company_id', companyId)
    .eq('category', 'time_based')
    .eq('is_active', true)
    .in('trigger_type', ['job_starting_soon', 'job_reminder', 'appointment_reminder'])

  if (!automations || automations.length === 0) {
    console.log('[automation] No active job automations found')
    return
  }

  console.log('[automation] Found', automations.length, 'active job automations to schedule')

  // Schedule each automation
  for (const automation of automations) {
    try {
      await _scheduleAutomation(
        automation.id,
        jobId,
        'job',
        new Date(scheduledDateTime).toISOString()
      )
      console.log('[automation] Successfully scheduled automation:', automation.name)
    } catch (error) {
      console.error(`[automation] Failed to schedule automation ${automation.name} for job ${jobId}:`, error)
      // Continue with other automations even if one fails
    }
  }
}

/**
 * Manually trigger an automation
 * Migrated from supabaseApi.js lines 338-399
 */
const _triggerAutomationManually = async (automationId, triggerRecordId = null, triggerRecordType = 'manual') => {
  console.log('[automation] triggerAutomationManually called:', { automationId, triggerRecordId, triggerRecordType })
  
  validateRequired(automationId, 'Automation ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  // Get the automation details
  const { data: automation, error: automationError } = await supabase
    .from('automations')
    .select('*')
    .eq('id', automationId)
    .eq('company_id', companyId)
    .single()

  if (automationError) {
    console.error('[automation] Error fetching automation:', automationError)
    throw automationError
  }
  
  if (!automation) {
    throw new Error('Automation not found')
  }

  // Create a manual trigger record in the scheduled queue
  const { data: queueRecord, error: queueError } = await supabase
    .from('scheduled_automation_queue')
    .insert([{
      automation_id: automationId,
      company_id: companyId,
      trigger_record_id: triggerRecordId || `manual_${Date.now()}`,
      trigger_record_type: triggerRecordType,
      scheduled_for: new Date().toISOString(),
      status: 'pending',
      created_by: 'manual_trigger'
    }])
    .select()
    .single()

  if (queueError) {
    console.error('[automation] Error creating queue record:', queueError)
    throw queueError
  }

  // Immediately invoke the automation runner to process this manual trigger
  const { data: runnerResult, error: runnerError } = await supabase.functions.invoke('scheduled-automation-runner', {
    body: { 
      manual_trigger: true,
      automation_id: automationId,
      queue_id: queueRecord.id
    }
  })

  if (runnerError) {
    console.error('[automation] Error invoking automation runner:', runnerError)
    throw runnerError
  }

  const result = { 
    success: true, 
    data: { 
      automation_name: automation.name,
      queue_id: queueRecord.id,
      runner_result: runnerResult
    }
  }

  console.log('[automation] triggerAutomationManually success for automation:', automation.name)
  return result
}

// Export functions with error handling
export const scheduleAutomation = withErrorHandling(_scheduleAutomation, 'schedule automation', 'automation')
export const scheduleJobAutomations = withErrorHandling(_scheduleJobAutomations, 'schedule job automations', 'job_automations')
export const triggerAutomationManually = withErrorHandling(_triggerAutomationManually, 'trigger automation manually', 'automation')